import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import classNames from 'classnames';
import { designSystem } from '@yola/ws-ui';
import getFontPreviewVariant from '../helpers/get-font-preview-variant';
import { HEADING_PREVIEW_TEXT, PARAGRAPH_PREVIEW_TEXT } from '../constants/text-samples';

const { PresetContainer, TextSkeleton } = designSystem;

const defaultVariables = {};

function FontPairPreset({
  id,
  title,
  fontsData,
  previewUrl = null,
  isSelected = false,
  observer,
  onClick,
  onEditClick = null,
  onDeleteClick = null,
  editLabel = '',
  deleteLabel = '',
  isCustom = false,
  isLoading = false,
  variables = defaultVariables,
}) {
  const presetRef = useRef(null);
  const isMobile = bowser.mobile;
  const { primaryFontFamily, secondaryFontFamily } = variables;
  let primaryFontWeight;
  let secondaryFontWeight;

  if (isCustom && !isLoading) {
    primaryFontWeight = getFontPreviewVariant(fontsData, primaryFontFamily);
    secondaryFontWeight = getFontPreviewVariant(fontsData, secondaryFontFamily);
  }

  useEffect(() => {
    observer.observe(presetRef.current);
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const handleClick = () => onClick(id);
  const handleEditClick = () => onEditClick(id);
  const handleDeleteClick = () => onDeleteClick(id);

  return (
    <div
      id={id}
      className={classNames('ws-font-pair-preset', isLoading && 'ws-font-pair-preset--loading')}
      ref={presetRef}
    >
      <PresetContainer
        title={title}
        isSelected={isSelected}
        {...(!isMobile && { withTitleTooltip: true })}
        onClick={handleClick}
        {...(onEditClick && {
          editLabel,
          onEditClick: handleEditClick,
        })}
        {...(isCustom &&
          onDeleteClick && {
            deleteLabel,
            onDeleteClick: handleDeleteClick,
          })}
      >
        {isCustom || isLoading ? (
          <div className="ws-font-pair-preset__custom">
            <h1
              className="ws-font-pair-preset__custom--heading"
              style={{ fontFamily: primaryFontFamily, fontWeight: primaryFontWeight }}
            >
              {isLoading ? <TextSkeleton width="70%" /> : HEADING_PREVIEW_TEXT}
            </h1>
            <p
              className="ws-font-pair-preset__custom--paragraph"
              style={{ fontFamily: secondaryFontFamily, fontWeight: secondaryFontWeight }}
            >
              {isLoading ? <TextSkeleton width="100%" lines={2} /> : PARAGRAPH_PREVIEW_TEXT}
            </p>
          </div>
        ) : (
          <img className="ws-font-pair-preset__image" src={previewUrl} alt={title} />
        )}
      </PresetContainer>
    </div>
  );
}

FontPairPreset.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  fontsData: PropTypes.arrayOf(
    PropTypes.shape({
      family: PropTypes.string,
      variants: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  previewUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  isSelected: PropTypes.bool,
  observer: PropTypes.shape({
    observe: PropTypes.func,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  onDeleteClick: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  editLabel: PropTypes.string,
  deleteLabel: PropTypes.string,
  isCustom: PropTypes.bool,
  variables: PropTypes.shape({
    primaryFontFamily: PropTypes.string,
    secondaryFontFamily: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
};

export default FontPairPreset;
